import classnames from "classnames";
import * as React from "react";
import "./DocumentStatus.scss";
import {
  getStatusText,
  isCompletedState,
  isHumanReadableStatusCompleted,
  isHumanReadableStatusFailed,
  isHumanReadableStatusProcessingOrUploading,
  isSuccessful,
  shouldHideHumanReadableStatusOutline
} from "./helpers";
import { SentBackIcon } from "./SentBackIcon";
import { StatusIcon } from "./StatusIcon";
import { BriefSpreadStatus, SPREAD_STATUSES } from "./types";
import { WarningIcon } from "./WarningIcon";

interface DocumentStatusProps {
  status: BriefSpreadStatus;
  humanReadableStatus?: string | undefined;
  hasManagedService: boolean;
  isLocked: boolean;
  rejected: boolean;
}

const statusIcon = (status: BriefSpreadStatus, rejected: boolean) => {
  if (rejected) {
    return <SentBackIcon />;
  }

  if (status === SPREAD_STATUSES.PROCESSING_FAILED) {
    return <WarningIcon />;
  }

  return <StatusIcon />;
};

const humanReadableStatusIcon = (
  humanReadableStatus: string,
  rejected: boolean
) => {
  if (rejected) {
    return <SentBackIcon />;
  }

  if (isHumanReadableStatusFailed(humanReadableStatus)) {
    return <WarningIcon />;
  }

  return <StatusIcon />;
};

const iconClassModifier = (status: BriefSpreadStatus, rejected: boolean) => {
  if (rejected) {
    return "rejected";
  }

  if (
    status === SPREAD_STATUSES.PROCESSING ||
    status === SPREAD_STATUSES.UPLOADING
  ) {
    return "disabled";
  } else if (status === SPREAD_STATUSES.PROCESSING_FAILED) {
    return "failed";
  } else if (isCompletedState(status)) {
    return "completed";
  } else {
    return "pendingReview";
  }
};

const humanReadableIconClassModifier = (
  humanReadableStatus: string,
  rejected: boolean
) => {
  if (rejected) {
    return "rejected";
  }

  if (isHumanReadableStatusProcessingOrUploading(humanReadableStatus)) {
    return "disabled";
  } else if (isHumanReadableStatusFailed(humanReadableStatus)) {
    return "failed";
  } else if (isHumanReadableStatusCompleted(humanReadableStatus)) {
    return "completed";
  } else {
    return "pendingReview";
  }
};

const renderLegacyStatus = (
  status: BriefSpreadStatus,
  hasManagedService: boolean,
  isLocked: boolean,
  rejected: boolean
) => (
  <div
    className={classnames("DocumentStatus", {
      "DocumentStatus--outline": isSuccessful(status)
    })}
  >
    <div className="DocumentStatus--grid">
      <div
        className={`DocumentStatus__icon DocumentStatus__icon--${iconClassModifier(
          status,
          rejected
        )}`}
      >
        {statusIcon(status, rejected)}
      </div>
      <div className="DocumentStatus__label">
        {getStatusText(hasManagedService, status, isLocked, rejected)}
      </div>
    </div>
  </div>
);

const renderHumanReadableStatus = (
  humanReadableStatus: string,
  rejected: boolean
) => (
  <div
    className={classnames("DocumentStatus", {
      "DocumentStatus--outline": !shouldHideHumanReadableStatusOutline(
        humanReadableStatus
      )
    })}
  >
    <div className="DocumentStatus--grid">
      <div
        className={`DocumentStatus__icon DocumentStatus__icon--${humanReadableIconClassModifier(
          humanReadableStatus,
          rejected
        )}`}
      >
        {humanReadableStatusIcon(humanReadableStatus, rejected)}
      </div>
      <div className="DocumentStatus__label">{humanReadableStatus}</div>
    </div>
  </div>
);

const DocumentStatus = ({
  status,
  hasManagedService,
  isLocked,
  rejected,
  humanReadableStatus
}: DocumentStatusProps) => {
  if (humanReadableStatus !== undefined) {
    return renderHumanReadableStatus(humanReadableStatus, rejected);
  }

  return renderLegacyStatus(status, hasManagedService, isLocked, rejected);
};

export default DocumentStatus;



// WEBPACK FOOTER //
// ./src/documentList/DocumentStatus.tsx